/**
 * Class representing a menu with toggleable submenus.
 */
class MenuHandler {
	/**
	 * Initializes all menus on the page.
	 */
	static setupMenu() {
		const menus = document.querySelectorAll('.menu');
		if (menus.length > 0) {
			menus.forEach((menuElement) => new MenuHandler(menuElement).initialize());
		}
	}

	/**
	 * Creates an instance of MenuHandler.
	 *
	 * @param {HTMLElement} menuElement - The menu element.
	 */
	constructor(menuElement) {
		this.menuElement = menuElement;
		this.openClass = 'open';
		this.boundHandleEscapeKeydown = this.handleEscapeKeydown.bind(this);
	}

	/**
	 * Initializes the menu by adding necessary event listeners.
	 */
	initialize() {
		this.menuElement.addEventListener('click', this.handleMenuClick.bind(this));
		document.addEventListener('click', this.handleOutsideClick.bind(this));
	}

	/**
	 * Handles the menu click event.
	 *
	 * @param {Event} event - The event object.
	 */
	handleMenuClick(event) {
		const link = event.target.closest('.menu-item-has-children > a');
		if (link && this.menuElement.contains(link)) {
			event.preventDefault();
			this.toggleSubmenu(link);
		}
	}

	/**
	 * Toggles the submenu visibility for the given link.
	 *
	 * @param {HTMLElement} link - The link element that was clicked.
	 */
	toggleSubmenu(link) {
		const parentItem = link.parentElement;
		const isOpening = !parentItem.classList.contains(this.openClass);
		this.closeOtherSubmenus(link);
		this.toggleMenuItem(parentItem, isOpening);
		isOpening ? this.addGlobalEventListeners() : this.removeGlobalEventListeners();
	}

	/**
	 * Handles clicks outside the menu to close open submenus.
	 *
	 * @param {Event} event - The event object.
	 */
	handleOutsideClick(event) {
		if (!this.menuElement.contains(event.target) && !event.target.classList.contains('mobile-toggle')) {
			this.closeAllSubmenus();
			this.removeGlobalEventListeners();
		}
	}

	/**
	 * Closes all open submenus.
	 */
	closeAllSubmenus() {
		this.menuElement.querySelectorAll(`.menu-item-has-children.${this.openClass}`).forEach((menuItem) => {
			this.toggleMenuItem(menuItem, false);
		});
	}

	/**
	 * Closes all submenus except the one containing the specified element.
	 *
	 * @param {HTMLElement} element - The element whose containing submenu should remain open.
	 */
	closeOtherSubmenus(element) {
		this.menuElement.querySelectorAll(`.menu-item-has-children.${this.openClass}`).forEach((menuItem) => {
			if (!menuItem.contains(element) && !element.contains(menuItem)) {
				this.toggleMenuItem(menuItem, false);
			}
		});
	}

	/**
	 * Toggles the open/close state of a menu item and its submenus.
	 *
	 * @param {HTMLElement} menuItem - The menu item to toggle.
	 * @param {boolean} isOpen - Whether the menu item should be opened or closed.
	 */
	toggleMenuItem(menuItem, isOpen) {
		menuItem.classList.toggle(this.openClass, isOpen);
		menuItem.querySelector('.sub-menu')?.classList.toggle(this.openClass, isOpen);
	}

	/**
	 * Adds global event listeners for handling clicks and keydown events.
	 */
	addGlobalEventListeners() {
		document.addEventListener('keydown', this.boundHandleEscapeKeydown);
	}

	/**
	 * Removes global event listeners for handling clicks and keydown events.
	 */
	removeGlobalEventListeners() {
		document.removeEventListener('keydown', this.boundHandleEscapeKeydown);
	}

	/**
	 * Handles the Escape keydown event to close all open submenus and set focus to the trigger.
	 *
	 * @param {KeyboardEvent} event - The event object.
	 */
	handleEscapeKeydown(event) {
		if (event.key === 'Escape') {
			const activeItem = this.menuElement.querySelector(`.${this.openClass}`);
			if (activeItem) {
				const trigger = activeItem.querySelector('a');
				this.closeAllSubmenus();
				if (trigger) {
					trigger.focus();
				}
				this.removeGlobalEventListeners();
			}
		}
	}
}

// Initialize menus when the document is ready
if (document.readyState === 'interactive' || document.readyState === 'complete') {
	MenuHandler.setupMenu();
} else {
	document.addEventListener('DOMContentLoaded', MenuHandler.setupMenu);
}
